import { catalogIndex } from "./catalog-index"
import { clearsEvidenceFloor, evidenceIndex } from "./evidence"
import { byId, byText } from "./order"
import { clampLevel, DEFAULT_POLICY, resolvePolicy } from "./policy"
import type { ResolvedPolicy } from "./policy"
import type {
    Catalog,
    Constraints,
    DueObligation,
    EmployeeProfile,
    Gap,
    Obligation,
    SkillLevel,
    SkillRequirement,
} from "./types"

/**
 * Step 2+3 of the pipeline: target profile, then gap.
 *
 * Deterministic on purpose — see the plan's "central design call". The LLM
 * never decides what is missing; it only reads prose upstream (to produce
 * `EmployeeProfile.skills`) and writes prose downstream (the rationale).
 */

// ---------------------------------------------------------------------------
// Dates — plain ISO arithmetic, no dependency, no `Date.now()`
// ---------------------------------------------------------------------------

/** Parse an ISO `YYYY-MM-DD` to a UTC timestamp. */
export function parseIsoDate(iso: string): number {
    const [y, m, d] = iso.split("-").map(Number)
    return Date.UTC(y, (m ?? 1) - 1, d ?? 1)
}

export function toIsoDate(ms: number): string {
    return new Date(ms).toISOString().slice(0, 10)
}

export function addMonths(iso: string, months: number): string {
    const date = new Date(parseIsoDate(iso))
    const targetMonth = date.getUTCMonth() + months
    const result = new Date(
        Date.UTC(date.getUTCFullYear(), targetMonth, 1, 0, 0, 0, 0),
    )
    // Clamp the day: 31 Jan + 1 month must be 28/29 Feb, not 2/3 March.
    const lastDayOfTarget = new Date(
        Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0),
    ).getUTCDate()
    result.setUTCDate(Math.min(date.getUTCDate(), lastDayOfTarget))
    return toIsoDate(result.getTime())
}

export function addDays(iso: string, days: number): string {
    return toIsoDate(parseIsoDate(iso) + days * 86_400_000)
}

// ---------------------------------------------------------------------------
// Target profile
// ---------------------------------------------------------------------------

/**
 * Merge role requirements with aspiration requirements.
 *
 * On a collision the HIGHER level wins, and the origin of the winner is kept —
 * with one exception: a `regulatorik` origin always survives, even if an
 * aspiration happens to demand the same skill at a higher level. Losing the
 * regulatory framing would let a mandatory item be presented as a career wish
 * and therefore be droppable, which is precisely the failure this engine is
 * built to prevent.
 */
export function buildTargetProfile(
    profile: EmployeeProfile,
    catalog: Catalog,
    policy: ResolvedPolicy = DEFAULT_POLICY,
): SkillRequirement[] {
    const roleReqs = catalog.roleRequirements[profile.roleId] ?? []
    const aspirationReqs = profile.aspiration
        ? (catalog.aspirationRequirements[profile.aspiration] ?? [])
        : []

    const merged = new Map<string, SkillRequirement>()
    for (const requirement of [...roleReqs, ...aspirationReqs]) {
        const existing = merged.get(requirement.skillId)
        if (!existing) {
            merged.set(requirement.skillId, requirement)
            continue
        }
        const keepRegulatorik =
            policy.targetProfile.regulatorikWins &&
            (existing.origin === "regulatorik" || requirement.origin === "regulatorik")
        merged.set(requirement.skillId, {
            skillId: requirement.skillId,
            level: Math.max(existing.level, requirement.level) as SkillLevel,
            origin: keepRegulatorik
                ? "regulatorik"
                : requirement.level > existing.level
                  ? requirement.origin
                  : existing.origin,
            obligationId: existing.obligationId ?? requirement.obligationId,
        })
    }
    return [...merged.values()]
}

// ---------------------------------------------------------------------------
// Current skill level
// ---------------------------------------------------------------------------

/**
 * The employee's effective level per skill: the highest reading from any
 * ALLOWED source, plus anything their completed Bausteine certify.
 *
 * The `allowedSources` filter is the "Datenquellen" toggle from the demo. It
 * lives here rather than in the UI so that switching MAG data off genuinely
 * changes the computation instead of merely hiding a column.
 */
export function resolveCurrentLevels(
    profile: EmployeeProfile,
    catalog: Catalog,
    constraints: Constraints,
): Map<string, { level: SkillLevel; evidence?: string; source?: Gap["evidenceSource"] }> {
    const policy = resolvePolicy(constraints.policy)
    const levels = new Map<
        string,
        { level: SkillLevel; evidence?: string; source?: Gap["evidenceSource"] }
    >()

    const consider = (
        skillId: string,
        level: SkillLevel,
        evidence?: string,
        source?: Gap["evidenceSource"],
    ) => {
        const existing = levels.get(skillId)
        if (existing && existing.level >= level) return
        levels.set(skillId, { level, evidence, source })
    }

    for (const holding of profile.skills) {
        // Two independent filters, deliberately. `allowedSources` is the hard
        // governance gate — a source that is switched off is not read at all.
        // `trust` is the softer editorial judgement on top: a self-assessment is
        // admissible but less credible than a passed exam, and an L&D team is
        // entitled to say so numerically rather than by silencing the source.
        if (!constraints.allowedSources.includes(holding.source)) continue
        const trust = policy.sources.trust[holding.source]
        if (trust < policy.sources.minTrust) continue

        if (policy.sources.mode !== "discount") {
            consider(holding.skillId, holding.level, holding.evidence, holding.source)
            continue
        }
        // Discounting can erase a reading entirely. Dropping it rather than
        // recording a level 0 keeps the gap's `evidence` honest: we did not
        // learn that they have none, we decided not to believe the claim.
        const discounted = clampLevel(holding.level * trust)
        if (discounted <= 0) continue
        consider(holding.skillId, discounted, holding.evidence, holding.source)
    }

    // Completed Bausteine are a fact of record rather than an inference, so no
    // SOURCE gating applies to them — but "attended" and "can do it" are not the
    // same claim, and `evidence.gateSkillCredit` is where that distinction gets
    // made. At the default floor of `abschluss` every completion still counts,
    // exactly as before.
    const { bausteinById } = catalogIndex(catalog)
    const evidence = evidenceIndex(profile.evidence)
    for (const completion of profile.completed) {
        const baustein = bausteinById.get(completion.bausteinId)
        if (!baustein) continue
        if (
            policy.evidence.gateSkillCredit &&
            !clearsEvidenceFloor(baustein, evidence.get(baustein.id), policy)
        ) {
            continue
        }
        for (const taught of baustein.teaches) {
            consider(
                taught.skillId,
                taught.level,
                `Abgeschlossen: ${baustein.title} (${completion.completedOn})`,
                "lernhistorie",
            )
        }
    }

    return levels
}

// ---------------------------------------------------------------------------
// Gaps
// ---------------------------------------------------------------------------

export function computeGaps(
    profile: EmployeeProfile,
    catalog: Catalog,
    constraints: Constraints,
): Gap[] {
    const policy = resolvePolicy(constraints.policy)
    const target = buildTargetProfile(profile, catalog, policy)
    const current = resolveCurrentLevels(profile, catalog, constraints)
    const { skillById } = catalogIndex(catalog)

    const gaps: Gap[] = []
    for (const requirement of target) {
        const skill = skillById.get(requirement.skillId)
        if (!skill) continue
        const held = current.get(requirement.skillId)
        const currentLevel = (held?.level ?? 0) as SkillLevel
        // At the default `minSize` of 1 over an integer scale this is exactly
        // `currentLevel >= requirement.level`. Raising it lets L&D say "half a
        // level short is not worth a course".
        if (requirement.level - currentLevel < policy.gaps.minSize) continue

        gaps.push({
            skillId: requirement.skillId,
            skillLabel: skill.label,
            category: skill.category,
            current: currentLevel,
            required: requirement.level,
            size: requirement.level - currentLevel,
            origin: requirement.origin,
            evidence: held?.evidence,
            evidenceSource: held?.source,
        })
    }

    // Stable ordering: regulatory first, then by gap size, then alphabetically.
    // Alphabetical last is what makes the whole engine reproducible — without a
    // total order, two runs could emit equal-priority gaps in either sequence.
    const originRank = policy.gaps.originRank
    return gaps.sort(
        (a, b) =>
            originRank[a.origin] - originRank[b.origin] ||
            b.size - a.size ||
            byText(a.skillLabel, b.skillLabel) ||
            byId(a.skillId, b.skillId),
    )
}

// ---------------------------------------------------------------------------
// Obligations
// ---------------------------------------------------------------------------

function bindsEmployee(obligation: Obligation, profile: EmployeeProfile): boolean {
    if (obligation.appliesToRoles.includes(profile.roleId)) return true
    if (
        obligation.appliesToSigningAuthority &&
        profile.signingAuthority &&
        obligation.appliesToSigningAuthority.includes(profile.signingAuthority)
    ) {
        return true
    }
    return false
}

/**
 * Which obligations are due at `asOf`.
 *
 * "Due" means: never discharged, or the last completion expires within the
 * obligation's lead time. The lead time is what turns a compliance deadline
 * into something plannable — a FIDLEG attestation lapsing in March has to
 * enter the plan in January, not in March.
 */
export function computeDueObligations(
    profile: EmployeeProfile,
    catalog: Catalog,
    constraints: Constraints,
): DueObligation[] {
    const policy = resolvePolicy(constraints.policy)
    const { bausteinById } = catalogIndex(catalog)
    const evidence = evidenceIndex(profile.evidence)

    /**
     * Completions that count as discharging a duty.
     *
     * This is FIDLEG Art. 6 as a boolean. The law asks a client advisor to
     * *evidence* knowledge of the conduct rules — so with the gate on, sitting
     * in the room no longer clears the obligation and a passed Nachweis does.
     * Off by default, because it is a claim about what the bank collects, not
     * about what the engine believes.
     */
    const discharges = (bausteinId: string): boolean => {
        if (!policy.evidence.gateObligationDischarge) return true
        const baustein = bausteinById.get(bausteinId)
        if (!baustein) return true
        return clearsEvidenceFloor(baustein, evidence.get(bausteinId), policy)
    }

    const completionByBaustein = new Map(
        profile.completed
            .filter((c) => discharges(c.bausteinId))
            .map((c) => [c.bausteinId, c.completedOn]),
    )
    const asOfMs = parseIsoDate(constraints.asOf)

    /**
     * How far ahead a duty enters the plan. The multiplier is one global dial
     * over each obligation's own lead time — an L&D team that wants more runway
     * everywhere should not have to edit nine legal records to get it. The
     * `=== 1` branch avoids the arithmetic entirely at default, so no rounding
     * can drift.
     */
    const leadTimeOf = (days: number) =>
        policy.obligations.leadTimeMultiplier === 1
            ? days
            : Math.round(days * policy.obligations.leadTimeMultiplier)

    const due: DueObligation[] = []
    for (const obligation of catalog.obligations) {
        if (!bindsEmployee(obligation, profile)) continue
        const leadTimeDays = leadTimeOf(obligation.leadTimeDays)

        const completedOn = completionByBaustein.get(obligation.bausteinId)
        if (!completedOn) {
            // Never done: due immediately, with the lead time as the grace window.
            due.push({
                obligation,
                expiresOn: null,
                dueBy: addDays(constraints.asOf, leadTimeDays),
                overdue: false,
            })
            continue
        }

        if (obligation.validForMonths === null) continue // one-off, discharged

        const expiresOn = addMonths(completedOn, obligation.validForMonths)
        const dueWindowOpens = addDays(expiresOn, -leadTimeDays)
        if (parseIsoDate(dueWindowOpens) > asOfMs) continue // not yet due

        due.push({
            obligation,
            expiresOn,
            dueBy: expiresOn,
            overdue: parseIsoDate(expiresOn) < asOfMs,
        })
    }

    return due.sort(
        (a, b) =>
            parseIsoDate(a.dueBy) - parseIsoDate(b.dueBy) ||
            byText(a.obligation.label, b.obligation.label) ||
            byId(a.obligation.id, b.obligation.id),
    )
}
